import React, { ReactNode, useContext, useEffect, useState } from 'react';
import {
  configurationUrl,
  defaultConfigurations,
  DynamicConfigurations,
} from './config';

interface DynamicConfigContext {
  config: DynamicConfigurations;
}

interface CreateConfigProviderProps {
  children: ReactNode;
}

const configContextObject = React.createContext<DynamicConfigContext>({
  config: defaultConfigurations,
});

const getWhiteLabel = () => {
  const hostname = window.location.hostname;
  if (hostname === 'robotic.inc') {
    return 'robotic_inc';
  } else if (hostname === 'dominion.ghmaritime.com') {
    return 'sentinel-proactive';
  } else {
    return defaultConfigurations.whitelabel;
  }
};

function mapDynamicConfigurations(
  config: DynamicConfigurations
): DynamicConfigurations {
  // Filter the fetched config object, to make sure only used variables are passed
  if (typeof config !== 'object' || config === null) {
    return defaultConfigurations;
  }

  const e2eTests = import.meta.env.VITE_E2E_TEST === 'true';
  config.dsn = e2eTests
    ? 'https://45728012ef289c31ed8aa2857efa3963@o4508240631889920.ingest.de.sentry.io/4508285550985296'
    : config.dsn;

  return {
    dsn: config?.dsn || defaultConfigurations.dsn,
    sentryEnvironment:
      config?.sentryEnvironment || defaultConfigurations.sentryEnvironment,
    whitelabel: getWhiteLabel(),
  };
}

export const ConfigContextProvider: React.FC<CreateConfigProviderProps> = ({
  children,
}) => {
  const [configState, setConfigState] = useState(defaultConfigurations);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetch(configurationUrl)
      .then((response) => {
        if (!response.ok) {
          throw new Error(
            `Error while fetching global config, using default values: ${configState}`
          );
        }
        return response.json();
      })
      .then((config: DynamicConfigurations) => {
        const filteredConfig = mapDynamicConfigurations(config);
        setConfigState(filteredConfig);
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  if (isLoading) {
    return null;
  }

  return (
    <configContextObject.Provider
      value={{
        config: configState,
      }}
    >
      {children}
    </configContextObject.Provider>
  );
};

export const useConfig = () => useContext(configContextObject);
